// In-game screens (the dusk interface). App-wide orientation on top of every
// tab: a phase timeline (where are we) + a task bar (what should I do now).
//   Kaart  — watchfire map + choose your post (the nightwatch); no chat here
//   Overleg— ALL chat: dorpsplein + per-location tabstrip, identity-coloured
//   Rol    — secret role card
//   Stem   — the evening vote (anonymous, public tally)
//   Nacht  — dimmed, locked; your confirmed post as overlay, no chat
//
// Client stays a dumb renderer: the firewall already strips other players'
// actions and the exact damage meter; here we only render what arrives. The
// identity colour is hashed client-side from the public id (team-neutral).

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type {
  BotStatusResponse,
  FireState,
  PlayerGameView,
  PostAction,
  PublicFire,
  PublicPlayer,
} from '@schemer/shared';
import { api, ApiError } from '../lib/api.js';
import { tokenStore } from '../lib/storage.js';
import { navigate } from '../lib/route.js';
import { getSocket } from '../lib/socket.js';
import { ensureSubscribed, getInitialPushState, type PushState } from '../lib/push.js';
import { hashId, playerColor } from '../lib/playerColor.js';
import { HelpContent } from './HelpScreen.js';
import '../game.css';

interface Props {
  code: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'view'; view: PlayerGameView }
  | { kind: 'error'; message: string };

type Tab = 'kaart' | 'overleg' | 'rol';

const PHASE_LABEL: Record<PlayerGameView['game']['phase'], string> = {
  morning: 'ochtend',
  day: 'dag',
  evening: 'schemering',
  night: 'nacht',
};

// Positions for up to 7 watchfires on the mini-map (asymmetric, evocative).
const FIRE_LAYOUT: ReadonlyArray<{ left: string; top: string }> = [
  { left: '26%', top: '30%' },
  { left: '68%', top: '26%' },
  { left: '44%', top: '58%' },
  { left: '78%', top: '64%' },
  { left: '20%', top: '68%' },
  { left: '58%', top: '82%' },
  { left: '84%', top: '42%' },
];

const FIRE_ST_LABEL: Record<FireState, string> = {
  brandt: 'brandt',
  flikkert: 'flikkert',
  gedoofd: 'gedoofd',
};

// Illustrated art (public/art/) replaces the old emoji/CSS placeholders.
export const FIRE_ART: Record<FireState, string> = {
  brandt: '/art/vuur-brandt.png',
  flikkert: '/art/vuur-flikkert.png',
  gedoofd: '/art/vuur-gedoofd.png',
};
export const ROLE_ART: Record<string, string> = {
  vuurwachter: '/art/rol-vuurwachter.png',
  ziener: '/art/rol-ziener.png',
  beschermer: '/art/rol-beschermer.png',
  shapeshifter: '/art/rol-shapeshifter.png',
  schaduw: '/art/rol-schaduw.png',
};

// Large 3:4 character portraits for the player's OWN role card. Every role has a
// woman/man version, so the variant is team-neutral and leaks nothing: the
// portrait is only ever rendered on your own role card, never for other players.
export const ROLE_PORTRAIT: Record<string, { v: string; m: string }> = {
  vuurwachter: { v: '/art/rol-vuurwachter-v.webp', m: '/art/rol-vuurwachter-m.webp' },
  ziener: { v: '/art/rol-ziener-v.webp', m: '/art/rol-ziener-m.webp' },
  beschermer: { v: '/art/rol-beschermer-v.webp', m: '/art/rol-beschermer-m.webp' },
  shapeshifter: { v: '/art/rol-shapeshifter-v.webp', m: '/art/rol-shapeshifter-m.webp' },
  schaduw: { v: '/art/rol-schaduw-v.webp', m: '/art/rol-schaduw-m.webp' },
};

/** Which portrait variant a player gets. v1: derived from the public player id, so
 *  it is stable across sessions and needs no server state. A player-picked avatar
 *  can replace this later without touching any of the art wiring below. */
export function portraitVariant(playerId: string): 'v' | 'm' {
  return hashId(playerId) % 2 === 0 ? 'v' : 'm';
}

// End-screen hero, by winning team.
export const END_ART: Record<string, string> = {
  eerlijken: '/art/einde-eerlijken.jpg',
  shapeshifters: '/art/einde-shapeshifters.jpg',
};

// Decorative round-2 assets. The parchment + masthead dress the morning Gazette
// (the catch-up summary); the flame divider separates sections; the ornate frame
// wreathes the character portrait on the role card.
export const DECOR_ART = {
  perkament: '/art/textuur-perkament.jpg',
  bode: '/art/banner-bode.webp',
  divider: '/art/divider-vlam.webp',
  frame: '/art/frame-sier.webp',
} as const;

// Illustrated crescent for the night screen's header — a free-floating icon with
// its own soft glow, replacing the plain CSS-drawn moon. (art/sfeer-maan.jpg, the
// full standing image, stays in the folder unused as a possible later backdrop.)
export const NIGHT_MOON_ART = '/art/maan-nacht.webp';

// Night backdrop. A dark veil is layered over it so the lights-out copy stays
// readable on top of the scene.
export const NIGHT_ART = '/art/sfeer-nacht.jpg';
const nightBackdrop: React.CSSProperties = {
  backgroundImage: `linear-gradient(rgba(9, 11, 18, 0.84), rgba(9, 11, 18, 0.94)), url('${NIGHT_ART}')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const ROLE_LABEL: Record<string, string> = {
  vuurwachter: 'Vuurwachter',
  ziener: 'Ziener',
  beschermer: 'Beschermer',
  shapeshifter: 'Shapeshifter',
  schaduw: 'Schaduw',
};

/**
 * Is the soft keyboard covering the screen right now?
 *
 * Two signals, on purpose. focus/blur on a composer is immediate and works
 * everywhere, but iOS Safari can leave focus set with no keyboard (and vice
 * versa), so visualViewport shrinking is the authoritative one. We also publish
 * the visual viewport height as --vvh: iOS shrinks the VISUAL viewport but not
 * the layout viewport, so a position:fixed app would otherwise keep its full
 * height and hide the composer behind the keyboard.
 *
 * On a desktop there is no keyboard overlay, so focus alone must NOT count —
 * otherwise clicking the chat box would hide the nav for mouse users.
 */
function useKeyboardOpen(): boolean {
  const [shrunk, setShrunk] = useState(false);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport ?? null;
    const sync = () => {
      if (!vv) return;
      document.documentElement.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
      // iOS also SCROLLS the visual viewport down to reveal the focused input.
      // A fixed element stays pinned to the layout viewport, so without this the
      // app sits above the visible area and you only see its bottom (the
      // composer) at the top of the screen.
      document.documentElement.style.setProperty('--vvtop', `${Math.round(vv.offsetTop)}px`);
      // A keyboard takes a big bite; toolbars only nibble, hence the threshold.
      setShrunk(window.innerHeight - vv.height > 120);
    };
    sync();
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);

    const inComposer = (t: EventTarget | null) =>
      t instanceof HTMLElement && !!t.closest('.composer');
    const onIn = (e: FocusEvent) => {
      if (inComposer(e.target)) setComposing(true);
    };
    const onOut = (e: FocusEvent) => {
      if (inComposer(e.target)) setComposing(false);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
      document.documentElement.style.removeProperty('--vvh');
      document.documentElement.style.removeProperty('--vvtop');
    };
  }, []);

  const coarse =
    typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
  return shrunk || (composing && coarse);
}

export function GameScreen({ code }: Props): React.JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Tab>('kaart');
  // "Hoe werkt het?" opens as an overlay so the game stays mounted + connected —
  // reading the rules mid-game must NOT count as leaving the game (briefing).
  const [showHelp, setShowHelp] = useState(false);
  // Unread dorpsplein chat: how many messages we'd counted as "seen". Persisted
  // per game so an async player sees what's new since their last visit.
  const [seenChatCount, setSeenChatCount] = useState<number | null>(null);
  // Whether this player has already seen the one-time role reveal for this game.
  const [roleSeen, setRoleSeen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(`schemer.roleSeen.${code}`) === '1';
    } catch {
      return false;
    }
  });
  const socketRef = useRef<ReturnType<typeof getSocket> | null>(null);
  // Tears down the reconnect + foreground resync listeners set up in the effect.
  const resyncCleanupRef = useRef<(() => void) | null>(null);
  // 1D: on the chat tab the orientation strip (timeline/mission/taskbar) collapses
  // so the bubbles get room; default collapsed, a tap expands it.
  const [orientOpen, setOrientOpen] = useState(false);
  // Mobile: while typing on the chat tab we give the keyboard the bottom of the
  // screen — nav out, composer up. Only there; Kaart/Rol keep their nav.
  const keyboardOpen = useKeyboardOpen();
  // 2D: a catch-up summary (mission, night events, votes, recent chat). Opens on
  // demand, and auto-pops once each morning so you never miss what happened.
  const [showSummary, setShowSummary] = useState(false);
  const autoSummaryDayRef = useRef<number | null>(null);
  // Who is composing right now, per channel: channelKey ('plein' | fireId) →
  // { playerId → displayName }. Each entry auto-expires (see the socket effect).
  const [typers, setTypers] = useState<Record<string, Record<string, string>>>({});
  const typerTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getState(code);
        if (cancelled) return;
        setState({ kind: 'view', view: res.view });

        const token = tokenStore.get();
        if (!token) return;
        const sock = getSocket();
        socketRef.current = sock;
        // Subscribing both joins the game room AND returns the current view, so
        // it doubles as a resync. Re-run it on every (re)connect: a dropped +
        // reconnected socket gets a fresh server session that is NOT in the room,
        // so without this it would silently miss every game:state (e.g. the
        // day→evening flip) and be stuck unable to vote.
        const subscribe = () => {
          sock.emit('game:subscribe', { code, deviceToken: token }, (ack) => {
            if (cancelled) return;
            if (!ack.ok) return setState({ kind: 'error', message: ack.error });
            setState({ kind: 'view', view: ack.view });
          });
        };
        subscribe();
        sock.io.on('reconnect', subscribe);

        // Belt-and-suspenders: pull a fresh state via REST when the tab returns
        // to the foreground (async play over days; the socket may have slept and
        // missed a broadcast). The server clock stays authoritative (rule 4).
        const resyncFromRest = () => {
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
          void api
            .getState(code)
            .then((r) => {
              if (!cancelled) setState({ kind: 'view', view: r.view });
            })
            .catch(() => {});
        };
        document.addEventListener('visibilitychange', resyncFromRest);
        window.addEventListener('focus', resyncFromRest);
        resyncCleanupRef.current = () => {
          sock.io.off('reconnect', subscribe);
          document.removeEventListener('visibilitychange', resyncFromRest);
          window.removeEventListener('focus', resyncFromRest);
        };
        const clearTyper = (ch: string, playerId: string) => {
          clearTimeout(typerTimers.current[`${ch}:${playerId}`]);
          setTypers((t) => {
            if (!t[ch]?.[playerId]) return t;
            const chMap = { ...t[ch] };
            delete chMap[playerId];
            return { ...t, [ch]: chMap };
          });
        };
        sock.on('game:state', (next) => {
          if (cancelled) return;
          setState({ kind: 'view', view: next });
          // A message arriving clears its author's "typing" indicator at once.
          const lastPlein = next.messages[next.messages.length - 1];
          if (lastPlein) clearTyper('plein', lastPlein.authorPlayerId);
          const lastLoc = next.locationMessages[next.locationMessages.length - 1];
          if (lastLoc && next.self.currentFireId) {
            clearTyper(next.self.currentFireId, lastLoc.authorPlayerId);
          }
        });
        sock.on('chat:typing', ({ fireId, playerId, displayName }) => {
          if (cancelled) return;
          const ch = fireId ?? 'plein';
          const tkey = `${ch}:${playerId}`;
          setTypers((t) => ({ ...t, [ch]: { ...(t[ch] ?? {}), [playerId]: displayName } }));
          clearTimeout(typerTimers.current[tkey]);
          typerTimers.current[tkey] = setTimeout(() => clearTyper(ch, playerId), 5000);
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: err instanceof ApiError ? err.message || err.code : 'kon het spel niet laden',
        });
      }
    })();
    return () => {
      cancelled = true;
      const sock = socketRef.current;
      if (sock) {
        sock.emit('game:unsubscribe');
        sock.off('game:state');
        sock.off('chat:typing');
      }
      resyncCleanupRef.current?.();
      resyncCleanupRef.current = null;
      for (const t of Object.values(typerTimers.current)) clearTimeout(t);
      typerTimers.current = {};
    };
  }, [code]);

  // 2D: auto-open the catch-up summary once when a new morning breaks (it reveals
  // what happened overnight). Guarded per day so it shows once, not every render.
  useEffect(() => {
    if (state.kind !== 'view') return;
    const g = state.view.game;
    if (g.status !== 'running' || g.phase !== 'morning') return;
    if (autoSummaryDayRef.current === g.dayNumber) return;
    autoSummaryDayRef.current = g.dayNumber;
    setShowSummary(true);
  }, [state]);

  // Track how many dorpsplein messages have been "seen": mark all seen while on
  // the Overleg tab (and persist), else settle a baseline from storage/current.
  useEffect(() => {
    if (state.kind !== 'view') return;
    const n = state.view.messages.length;
    setSeenChatCount((prev) => {
      if (tab === 'overleg') {
        try {
          localStorage.setItem(`schemer.seenChat.${code}`, String(n));
        } catch {
          /* private mode */
        }
        return n;
      }
      if (prev !== null) return prev;
      let stored: number | null = null;
      try {
        const raw = localStorage.getItem(`schemer.seenChat.${code}`);
        if (raw != null) stored = Number(raw);
      } catch {
        /* private mode */
      }
      // No stored baseline yet (first time in this game) → treat everything as
      // unread so existing chat is flagged; opening Overleg then marks it seen.
      return stored ?? 0;
    });
  }, [state, tab, code]);

  const refresh = (view: PlayerGameView) => setState({ kind: 'view', view });

  if (state.kind === 'loading') {
    return (
      <div className="gm">
        <div className="gm-content">
          <p className="sub">spel laden…</p>
        </div>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="gm">
        <div className="gm-content">
          <button className="back" onClick={() => navigate({ name: 'splash' })}>
            ‹ terug
          </button>
          <p className="sub">{state.message}</p>
        </div>
      </div>
    );
  }

  const { view } = state;
  // One-time role reveal when the game starts — flip a card to see your secret role.
  if (view.game.status === 'running' && !roleSeen) {
    return (
      <RoleReveal
        view={view}
        onEnter={() => {
          try {
            localStorage.setItem(`schemer.roleSeen.${code}`, '1');
          } catch {
            /* private mode */
          }
          setRoleSeen(true);
        }}
      />
    );
  }
  if (view.game.status === 'finished')
    return <GameOver view={view} code={code} onRefresh={refresh} typers={typers.plein} />;
  if (view.game.phase === 'night') return <NightView view={view} code={code} onRefresh={refresh} />;

  // New dorpsplein messages since last seen — the "er wordt gekletst"-badge.
  const newChatCount =
    seenChatCount !== null && tab !== 'overleg'
      ? Math.max(0, view.messages.length - seenChatCount)
      : 0;

  const canPost = view.game.phase === 'day' || view.game.phase === 'evening';
  const needsPost = canPost && !view.self.pendingPost && view.self.isAlive;
  const needsVote = view.game.phase === 'evening' && !view.self.hasVotedToday && view.self.isAlive;

  return (
    <div
      className={`gm phase-${view.game.phase}${tab === 'overleg' && keyboardOpen ? ' kb-open' : ''}`}
    >
      <TopBar view={view} onHelp={() => setShowHelp(true)} onSummary={() => setShowSummary(true)} />
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showSummary && <SummaryModal view={view} onClose={() => setShowSummary(false)} />}
      {/* On the chat tab the page itself doesn't scroll — only the message
          stream does, so the header + composer stay put (WhatsApp-style). */}
      <div className={`gm-content ${tab === 'overleg' ? 'chat' : ''}`}>
        {/* App-wide orientation: where are we (day timeline + mission) + what to do.
            On the chat tab it collapses to a slim bar (1D) to free up room. */}
        {tab === 'overleg' && !orientOpen ? (
          <CollapsedOrientation view={view} onExpand={() => setOrientOpen(true)} />
        ) : (
          <>
            {/* P1: one "status + one action" panel instead of three stacked cards. */}
            <div className="statuspanel">
              <PhaseTimeline view={view} />
              <MissionBar view={view} />
              <TaskBar view={view} onGo={setTab} />
            </div>
            {/* Test-mode only (renders nothing without bots): is Claude live? */}
            {(import.meta.env.DEV || view.game.schedule.clockMode === 'fast') && (
              <BotStatus code={code} />
            )}
            {tab === 'overleg' && (
              <button
                className="orient-collapse"
                onClick={() => setOrientOpen(false)}
                aria-expanded={true}
              >
                ▴ inklappen
              </button>
            )}
          </>
        )}

        {/* Kaart & acties (1C): the map IS the doing surface — post by day, vote at
            dusk, both here rather than in separate tabs. */}
        {tab === 'kaart' && (
          <>
            <HubView view={view} />
            {view.game.phase === 'evening' && (
              <VoteView view={view} code={code} onRefresh={refresh} />
            )}
            {canPost && <PostView view={view} code={code} onRefresh={refresh} />}
          </>
        )}
        {tab === 'overleg' && (
          <OverlegView view={view} code={code} onRefresh={refresh} typers={typers} />
        )}
        {tab === 'rol' && <RolView view={view} code={code} onRefresh={refresh} />}
      </div>
      <nav className={`gm-nav${tab === 'overleg' && keyboardOpen ? ' kb-hidden' : ''}`}>
        <NavBtn
          label="Kaart"
          icon="kaart"
          on={tab === 'kaart'}
          badge={needsPost || needsVote}
          onClick={() => setTab('kaart')}
        />
        <NavBtn
          label="Overleg"
          icon="overleg"
          on={tab === 'overleg'}
          count={newChatCount}
          onClick={() => setTab('overleg')}
        />
        <NavBtn label="Rol" icon="rol" on={tab === 'rol'} onClick={() => setTab('rol')} />
      </nav>
    </div>
  );
}

type IconName = 'kaart' | 'overleg' | 'rol' | 'post' | 'stem';

const NAV_ART: Record<IconName, string> = {
  kaart: '/art/nav-kaart.png',
  overleg: '/art/nav-overleg.png',
  rol: '/art/nav-rol.png',
  post: '/art/nav-post.png',
  stem: '/art/nav-post.png',
};

function NavBtn({
  label,
  icon,
  on,
  badge,
  count,
  onClick,
}: {
  label: string;
  icon: IconName;
  on: boolean;
  badge?: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button className={on ? 'on' : ''} onClick={onClick}>
      <span className="ic">
        <img className="nav-art" src={NAV_ART[icon]} alt="" width={30} height={30} />
        {count && count > 0 ? (
          <span className="badge chat" aria-label={`${count} nieuwe berichten`}>
            {count > 9 ? '9+' : count}
          </span>
        ) : (
          badge && <span className="badge">!</span>
        )}
      </span>
      {label}
    </button>
  );
}

function TopBar({
  view,
  onHelp,
  onSummary,
}: {
  view: PlayerGameView;
  onHelp: () => void;
  onSummary: () => void;
}) {
  return (
    <header className="gm-topbar">
      <div className="place">Willowbrook</div>
      <div className="phase">
        Dag {view.game.dayNumber} · {PHASE_LABEL[view.game.phase]}
      </div>
      {/* 14: the single countdown lives in the status panel (next to the phase and
          the action it belongs to); the header no longer carries a second clock. */}
      <div className="gm-topbar-right">
        {/* P3: a newspaper reads as "the Bode / what happened" far quicker than
            the old clipboard — and it matches the Gazette the button opens. */}
        <button
          className="gm-help-btn"
          onClick={onSummary}
          aria-label="De Willowbrook Bode: wat is er gebeurd?"
          title="De Bode — wat is er gebeurd?"
        >
          📰
        </button>
        <button
          className="gm-help-btn"
          onClick={onHelp}
          aria-label="Hoe werkt het?"
          title="Hoe werkt het?"
        >
          ?
        </button>
      </div>
    </header>
  );
}

// 2D — catch-up summary: what happened overnight, the mission stand, the vote,
// and the last few lines. All from the already-filtered view (no secret leak).
// Auto-opens each morning and via the topbar 📋 button.
function SummaryModal({ view, onClose }: { view: PlayerGameView; onClose: () => void }) {
  const g = view.game;
  const alive = view.players.filter((p) => p.isAlive).length;
  const litFires = view.fires.filter((f) => f.state !== 'gedoofd').length;
  const events = view.events.slice(0, 8);
  const tally = [...view.voteTally].sort((a, b) => b.count - a.count);
  const recentChat = view.messages.slice(-5);
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.displayName ?? '?';
  return (
    <div
      className="help-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Samenvatting"
      onClick={onClose}
    >
      <div className="help-modal gazette" onClick={(e) => e.stopPropagation()}>
        <header className="gz-masthead">
          <img src={DECOR_ART.bode} alt="De Willowbrook Bode" />
          <button className="help-close gz-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </header>
        <p className="gz-edition">
          {g.phase === 'morning' ? 'De ochtend onthult' : 'Waar staan we?'} · dag {g.dayNumber}
        </p>
        <div className="help-modal-scroll">
          <div className="reveal">
            <div className="lab">Missie</div>
            <p className="sub">
              Dag {g.dayNumber} van {g.missionLength} · {litFires} van {view.fires.length} vuren aan
              · {alive} spelers in het spel · verlies bij {g.doofDrempel} gedoofd.
            </p>
          </div>
          {events.length > 0 && (
            <div className="reveal" style={{ marginTop: 12 }}>
              <div className="lab">Wat er gebeurde</div>
              <ul>
                {events.map((e) => (
                  <li key={e.id}>{e.text}</li>
                ))}
              </ul>
            </div>
          )}
          {tally.length > 0 && (
            <div className="reveal" style={{ marginTop: 12 }}>
              <div className="lab">Stemming</div>
              <ul>
                {tally.map((t) => (
                  <li key={t.targetPlayerId}>
                    {nameOf(t.targetPlayerId)}: {t.count}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recentChat.length > 0 && (
            <div className="reveal" style={{ marginTop: 12 }}>
              <div className="lab">Laatst op het plein</div>
              <ul>
                {recentChat.map((m) => (
                  <li key={m.id}>
                    <strong style={{ color: playerColor(m.authorPlayerId) }}>
                      {nameOf(m.authorPlayerId)}:
                    </strong>{' '}
                    {m.body}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// In-game rules overlay. Rendered on top of the mounted game so the socket +
// state survive — closing returns you exactly where you were (briefing).
function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="help-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Hoe werkt het?"
      onClick={onClose}
    >
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <header className="help-modal-bar">
          <h1>Hoe werkt het?</h1>
          <button className="help-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </header>
        <div className="help-modal-scroll">
          {/* In-game: the animation opens in a new tab so the running game isn't
              reloaded/reconnected just to watch a clip. */}
          <HelpContent animInNewTab />
        </div>
      </div>
    </div>
  );
}

// ---------- Orientation: phase timeline (1B) + task bar (1A) ----------

type Phase = PlayerGameView['game']['phase'];
const PHASE_ORDER: Phase[] = ['morning', 'day', 'evening', 'night'];
const PHASE_NAME: Record<Phase, string> = {
  morning: 'Ochtend',
  day: 'Dag',
  evening: 'Schemer',
  night: 'Nacht',
};
const PHASE_EXPLAIN: Record<Phase, string> = {
  morning: 'De ochtend onthult wat er ’s nachts gebeurde — jouw bewijs.',
  day: 'Praat en beschuldig op het dorpsplein, en kies je post voor vannacht.',
  evening: 'De posten liggen vast. Laatste overleg én de stemronde.',
  night: 'Alles op slot — alleen geheime acties tellen; de vuren worden bij dageraad opgelost.',
};

// 1B — where are we in the day. Server-owned clock (golden rule 4): we only read
// the phase + phaseEndsAt and count down visually. Tap a phase → in-place explainer.
function PhaseTimeline({ view }: { view: PlayerGameView }) {
  const now = view.game.phase;
  const [open, setOpen] = useState<Phase | null>(null);
  const nowIdx = PHASE_ORDER.indexOf(now);
  return (
    <div className="timeline">
      <div className="tl-track">
        {PHASE_ORDER.map((p, i) => {
          const state = i < nowIdx ? 'done' : i === nowIdx ? 'now' : 'soon';
          return (
            <button
              key={p}
              className={`tl-step ${state} ${open === p ? 'open' : ''}`}
              onClick={() => setOpen(open === p ? null : p)}
            >
              <span className="tl-dot" />
              <span className="tl-nm">{PHASE_NAME[p]}</span>
            </button>
          );
        })}
      </div>
      {open && (
        <div className="tl-pop">
          <span className="tl-when">
            {PHASE_ORDER.indexOf(open) < nowIdx ? 'geweest' : open === now ? 'nu' : 'straks'}
          </span>
          {PHASE_EXPLAIN[open]}
        </div>
      )}
    </div>
  );
}

// 1D — the collapsed orientation on the chat tab: a slim tappable bar with the
// phase, countdown and any pending action, so the message stream gets the room.
function CollapsedOrientation({ view, onExpand }: { view: PlayerGameView; onExpand: () => void }) {
  const countdown = useCountdown(view.game.phaseEndsAt);
  const phase = view.game.phase;
  const next = PHASE_ORDER[PHASE_ORDER.indexOf(phase) + 1];
  const me = view.self;
  const todo =
    phase === 'evening' && !me.hasVotedToday && me.isAlive
      ? 'nog stemmen'
      : phase === 'day' && !me.pendingPost && me.isAlive
        ? 'nog posten'
        : null;
  return (
    <button
      className="orient-bar"
      onClick={onExpand}
      aria-expanded={false}
      aria-label="toon dag-info"
    >
      <span className={`ob-dot phase-dot-${phase}`} />
      <span className="ob-phase">Dag {view.game.dayNumber}</span>
      <span className="ob-sub">
        {PHASE_NAME[phase]} · nog {countdown}
        {next ? ` tot ${PHASE_NAME[next]}` : ''}
      </span>
      {todo && <span className="ob-todo">{todo}</span>}
      <span className="ob-chev" aria-hidden="true">
        ▾
      </span>
    </button>
  );
}

// 1F — mission progress: a second timescale next to the day timeline. How many
// days must the chain survive, and how are the fires holding. Client-side from
// the already-filtered state (day, mission length, doof-drempel, doused count).
function MissionBar({ view }: { view: PlayerGameView }) {
  const { dayNumber, missionLength, doofDrempel, dousedCount, phase } = view.game;
  const countdown = useCountdown(view.game.phaseEndsAt);
  const totalFires = view.fires.length;
  const litNow = totalFires - dousedCount;
  const flikkert = view.fires.filter((f) => f.state === 'flikkert').length;
  const day = Math.min(dayNumber, missionLength);
  const daysLeft = Math.max(0, missionLength - dayNumber);
  return (
    <div className="mission">
      {/* P1: day, phase and countdown on one line — the timeline above no longer
          repeats "Nu: … nog …", and this is the single home for the fire stand. */}
      <div className="ms-head">
        <span className="ms-day">
          Dag {day} van {missionLength}
        </span>
        <span className="ms-dots" aria-hidden="true">
          {Array.from({ length: missionLength }, (_, i) => (
            <i key={i} className={i < dayNumber ? 'on' : ''} />
          ))}
        </span>
        <span className="ms-now">
          {PHASE_NAME[phase]} · nog <b>{countdown}</b>
        </span>
      </div>
      <div className="ms-sub">
        🔥 {litNow} van {totalFires} vuren aan
        {flikkert > 0 && ` · ${flikkert} ${flikkert === 1 ? 'flikkert' : 'flikkeren'}`} ·{' '}
        {daysLeft > 0
          ? `nog ${daysLeft} ${daysLeft === 1 ? 'dag' : 'dagen'} volhouden`
          : 'laatste dag'}{' '}
        · verlies bij {doofDrempel} gedoofd.
      </div>
    </div>
  );
}

// 1A — what does THIS player have to do right now (one action + deadline).
// Derived purely from the phase + own action flags; no new secret state.
function TaskBar({ view, onGo }: { view: PlayerGameView; onGo: (t: Tab) => void }) {
  const me = view.self;
  const phase = view.game.phase;

  if (!me.isAlive) {
    return (
      <div className="taskbar dead">
        <div className="tb-txt">
          <b>Je bent weggestuurd.</b>
          <span>je kijkt mee vanaf de zijlijn — je rol blijft geheim</span>
        </div>
      </div>
    );
  }
  if (phase === 'morning') {
    return (
      <div className="taskbar info">
        <div className="tb-txt">
          <b>De ochtend onthult wat er ’s nachts gebeurde.</b>
          <span>een gedoofd vuur wijst naar wie daar postte — jouw bewijs</span>
        </div>
      </div>
    );
  }
  if (phase === 'evening' && !me.hasVotedToday) {
    return (
      <div className="taskbar act">
        <div className="tb-txt">
          <b>Stem anoniem wie je verdenkt.</b>
          <span>de telling is openbaar</span>
        </div>
        <button className="tb-go" onClick={() => onGo('kaart')}>
          Stem →
        </button>
      </div>
    );
  }
  if (!me.pendingPost) {
    return (
      <div className="taskbar act">
        <div className="tb-txt">
          <b>Kies waar je vannacht de wacht houdt.</b>
          <span>kies vóór de schemer valt</span>
        </div>
        <button className="tb-go" onClick={() => onGo('kaart')}>
          Kies je post →
        </button>
      </div>
    );
  }
  const postName = view.fires.find((f) => f.id === me.pendingPost!.fireId)?.name;
  return (
    <div className="taskbar done">
      <div className="tb-txt">
        <b>✓ Je post staat: {postName}.</b>
        <span>je mag nog wisselen tot de schemer</span>
      </div>
    </div>
  );
}

// ---------- Kaart / hub (posting surface, 1C) ----------

function HubView({ view }: { view: PlayerGameView }) {
  const fires = view.fires;

  return (
    <>
      {/* P1 dedup: the fire stand lives in the mission line above, not here too. */}
      <h2 className="vt">De wachtvuren</h2>

      <div className="map" style={{ marginTop: 12, height: 200 }}>
        <div className="terrain" />
        {fires.map((f, i) => {
          const pos = FIRE_LAYOUT[i % FIRE_LAYOUT.length]!;
          return (
            <div key={f.id}>
              <div
                className={`fire ${f.state}${f.kwetsbaar ? ' kwetsbaar' : ''}`}
                style={{ left: pos.left, top: pos.top }}
              >
                <img className="flame-art" src={FIRE_ART[f.state]} alt="" width={48} height={48} />
                <div className="nm">
                  {f.name}
                  {f.kwetsbaar && <span className="kw">⚠</span>}
                </div>
              </div>
              {/* P2: one coloured token per occupant instead of a single generic
                  dot, so the map shows WHO stands where, not just "someone". */}
              {f.occupantPlayerIds.length > 0 && (
                <div className="toks" style={{ left: pos.left, top: `calc(${pos.top} + 30px)` }}>
                  {f.occupantPlayerIds.slice(0, 5).map((id) => (
                    <i key={id} className="tok" style={{ background: playerColor(id) }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="maphint">plaatsing = bewijs</div>
      </div>

      {view.game.phase === 'morning' && <OnthullingCard view={view} />}
    </>
  );
}

function OnthullingCard({ view }: { view: PlayerGameView }) {
  // The morning reveal: last night's resolution lines (this day's events).
  const lines = view.events
    .filter((e) =>
      [
        'fire_doused',
        'fire_flickers',
        'fire_recovered',
        'player_voted_out',
        'no_elimination',
        // Private to the framed feeder — the firewall only delivers it to them.
        'nat_hout_privaat',
      ].includes(e.type),
    )
    .slice(0, 6);
  if (lines.length === 0) return null;
  return (
    <div className="reveal" style={{ marginTop: 12 }}>
      <div className="lab">🌅 De ochtend onthult</div>
      <ul>
        {lines.map((e) =>
          e.type === 'nat_hout_privaat' ? (
            <li key={e.id} className="privaat">
              🍂 <em>Alleen voor jou:</em> {e.text}
            </li>
          ) : (
            <li key={e.id}>{e.text}</li>
          ),
        )}
      </ul>
    </div>
  );
}

function FirePresence({
  fire,
  players,
  selfId,
}: {
  fire: PublicFire;
  players: PublicPlayer[];
  selfId: string;
}) {
  // 16: no "leeg" chip — the card's "0/3 plekken" line already says this.
  if (fire.occupantPlayerIds.length === 0) return null;
  return (
    <div className="presence">
      {/* P2: the per-player identity colour (same as the chat bubbles) so
          "who posted where" reads at a glance — placement is the evidence. */}
      {fire.occupantPlayerIds.slice(0, 5).map((id) => (
        <span
          key={id}
          className={`avatar ${id === selfId ? 'me' : ''}`}
          style={{ color: playerColor(id), borderColor: playerColor(id) }}
          title={players.find((p) => p.id === id)?.displayName ?? ''}
        >
          {initials(players.find((p) => p.id === id)?.displayName)}
        </span>
      ))}
    </div>
  );
}

// ---------- Role reveal (one-time, on game start) ----------

function RoleReveal({ view, onEnter }: { view: PlayerGameView; onEnter: () => void }) {
  const { self } = view;
  const shifter = self.team === 'shapeshifters';
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="gm reveal-screen">
      <div className="reveal-inner">
        <h2 className="vt">Het spel begint</h2>
        <div className="sub">Dit is jouw geheime rol — laat niemand meekijken.</div>

        <div
          className={`flip-card ${flipped ? 'flipped' : ''}`}
          role="button"
          tabIndex={0}
          aria-label={flipped ? `Jouw rol: ${self.role}` : 'Tik om je rol te onthullen'}
          onClick={() => setFlipped(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setFlipped(true);
            }
          }}
        >
          <div className="flip-inner">
            <div className="flip-face flip-back">
              <div className="flip-q">?</div>
              <div className="flip-hint">tik om te onthullen</div>
            </div>
            <div className={`flip-face flip-front ${self.team}`}>
              {/* The card is exactly 3:4, same as the portraits, so the character
                  art fills the face without cropping. */}
              <img
                className="flip-portrait"
                src={
                  (ROLE_PORTRAIT[self.role] ?? ROLE_PORTRAIT.vuurwachter!)[
                    self.avatar ?? portraitVariant(self.id)
                  ]
                }
                alt={`Rol: ${ROLE_LABEL[self.role] ?? self.role}`}
                width={224}
                height={296}
              />
              <div className="flip-caption">
                <div className="rn">{self.role}</div>
                <div className={`team ${self.team}`}>
                  {shifter ? 'De bedriegers' : 'De eerlijken'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {flipped && (
          <div className="reveal-after">
            <div className="card">
              <div className="lab" style={{ margin: '0 0 5px' }}>
                Wat kun je doen?
              </div>
              <div className="sub">{roleCanDo(self.role)}</div>
            </div>
            <button className="cta" onClick={onEnter}>
              <div className="txt">
                <b>Betreed Willowbrook</b>
                <div>je rol blijft geheim tot het eind</div>
              </div>
              <div className="arw">›</div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Rol ----------

function roleCanDo(role: string): string {
  switch (role) {
    case 'shapeshifter':
      return 'Post ’s nachts bij een vuur en laat het in het geheim doven. Doe overdag alsof je voedt. Vind gaandeweg je mede-shapeshifters.';
    case 'schaduw':
      return 'Saboteer als een shapeshifter — én speel één keer per potje “nat hout”: mik het op een onschuldige, dan sputtert diens vuur en lijkt díé de saboteur.';
    case 'ziener':
      return 'Post overdag zoals iedereen — én onderzoek ’s nachts in het geheim één speler. De volgende ochtend weet je of die een shapeshifter is. Zet die kennis subtiel in.';
    case 'beschermer':
      return 'Post zoals iedereen, en scherm ’s nachts één vuur af zodat sabotage daar sputtert.';
    default:
      return 'Post ’s nachts bij een vuur om het brandend te houden. Praat, beschuldig en stem om de shapeshifters te ontmaskeren.';
  }
}

function RolView({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const { self } = view;
  const shifter = self.team === 'shapeshifters';
  return (
    <>
      <h2 className="vt" style={{ textAlign: 'center', width: '100%' }}>
        Jouw rol · geheim
      </h2>
      <div className={`hero ${self.team}`} style={{ marginTop: 10 }}>
        <div className="portrait-frame">
          <img
            className="role-portrait"
            src={
              (ROLE_PORTRAIT[self.role] ?? ROLE_PORTRAIT.vuurwachter!)[
                // Their own pick when they made one; otherwise a stable id-derived variant.
                self.avatar ?? portraitVariant(self.id)
              ]
            }
            alt={`Rol: ${ROLE_LABEL[self.role] ?? self.role}`}
            width={210}
            height={280}
            loading="lazy"
          />
          <img className="frame-ornament" src={DECOR_ART.frame} alt="" aria-hidden="true" />
        </div>
        {/* P2: no mini-emblem here — beside the framed portrait and the serif
            name it read as clutter. The emblem still lives in the nav and the
            end-game reveal. */}
        <div className="rn">{self.role}</div>
        <div className={`team ${self.team}`}>{shifter ? 'De bedriegers' : 'De eerlijken'}</div>
      </div>

      <img className="flame-divider" src={DECOR_ART.divider} alt="" loading="lazy" />

      <div className="card" style={{ marginTop: 12 }}>
        <div className="lab" style={{ margin: '0 0 5px' }}>
          Wat kun je doen?
        </div>
        <div className="sub">{roleCanDo(self.role)}</div>
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <div className="lab" style={{ margin: '0 0 5px' }}>
          Wanneer win je?
        </div>
        <div className="sub">
          {shifter
            ? `Met de bedriegers, als ${view.game.doofDrempel} vuren doven vóór de missie klaar is.`
            : `Met de eerlijken, als de keten ${view.game.missionLength} dagen brandend blijft (of alle shapeshifters weg zijn).`}
        </div>
      </div>

      {self.role === 'ziener' && view.game.modules.ziener && (
        <ZienerPanel view={view} code={code} onRefresh={onRefresh} />
      )}
      {self.role === 'beschermer' && view.game.modules.beschermer && (
        <BeschermerPanel view={view} code={code} onRefresh={onRefresh} />
      )}
      {self.role === 'schaduw' && view.game.modules.schaduw && (
        <SchaduwPanel view={view} code={code} onRefresh={onRefresh} />
      )}

      <div className="secret" style={{ marginTop: 12 }}>
        Alleen jij ziet dit — houd het geheim.
      </div>

      <SettingsFooter view={view} code={code} />
    </>
  );
}

function ZienerPanel({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const me = view.self;
  const canPick = me.isAlive && (view.game.phase === 'day' || view.game.phase === 'evening');
  const others = view.players.filter((p) => p.isAlive && p.id !== me.id);
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.displayName ?? '?';

  async function investigate(targetPlayerId: string) {
    setBusy(targetPlayerId);
    try {
      const r = await api.investigate(code, { targetPlayerId });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'onderzoek mislukt');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 10 }}>
      <div className="lab" style={{ margin: '0 0 5px' }}>
        🔮 Onderzoek
      </div>

      {me.lastInvestigation && (
        <>
          <div
            className="sub"
            style={{
              marginBottom: 6,
              color: me.lastInvestigation.isShapeshifter ? '#e98798' : '#a6c98c',
            }}
          >
            Je onderzocht <strong>{nameOf(me.lastInvestigation.targetPlayerId)}</strong> —{' '}
            {me.lastInvestigation.isShapeshifter ? 'een shapeshifter! 🐺' : 'geen shapeshifter.'}
          </div>
          <div className="coach">
            {me.lastInvestigation.isShapeshifter
              ? '🤫 Deel dit voorzichtig. Zeg niet “de Ziener zag het” — stuur de verdenking subtiel, anders ben jij hun volgende doelwit.'
              : '🤫 Kruis ’m van je lijst — maar verklap niet dat jij de Ziener bent. Hou je bron geheim.'}
          </div>
        </>
      )}

      {canPick ? (
        <>
          <div className="sub" style={{ marginBottom: 8 }}>
            Kies wie je vannacht onderzoekt — de uitslag komt morgenochtend.
          </div>
          <div className="coach" style={{ marginBottom: 8 }}>
            🤫 Onderzoek iemand die je publiekelijk (nog) niet durft te beschuldigen — en blijf zelf
            onopvallend.
          </div>
          <div className="gap8">
            {others.map((p) => {
              const picked = me.pendingInvestigationTargetId === p.id;
              return (
                <div key={p.id} className="tally">
                  <span>{p.displayName}</span>
                  <button
                    className="vote"
                    disabled={busy !== null}
                    onClick={() => void investigate(p.id)}
                  >
                    {busy === p.id ? '…' : picked ? '✓ gekozen' : 'onderzoek'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="sub">Je kunt overdag iemand kiezen om ’s nachts te onderzoeken.</div>
      )}
    </div>
  );
}

function BeschermerPanel({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const me = view.self;
  const canPick = me.isAlive && (view.game.phase === 'day' || view.game.phase === 'evening');
  const fires = view.fires.filter((f) => f.state !== 'gedoofd');

  async function protect(fireId: string) {
    setBusy(fireId);
    try {
      const r = await api.protect(code, { fireId });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'afschermen mislukt');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 10 }}>
      <div className="lab" style={{ margin: '0 0 5px' }}>
        🛡️ Afschermen
      </div>
      {canPick ? (
        <>
          <div className="sub" style={{ marginBottom: 8 }}>
            Kies één vuur om vannacht af te schermen — sabotage sputtert daar. Niet twee nachten
            hetzelfde vuur.
          </div>
          <div className="gap8">
            {fires.map((f) => {
              const picked = me.pendingProtectionFireId === f.id;
              return (
                <div key={f.id} className="tally">
                  <span>
                    {f.name}
                    <span className={`cnt st ${f.state}`} style={{ marginLeft: 8 }}>
                      {FIRE_ST_LABEL[f.state]}
                    </span>
                  </span>
                  <button
                    className="vote"
                    disabled={busy !== null}
                    onClick={() => void protect(f.id)}
                  >
                    {busy === f.id ? '…' : picked ? '✓ beschermd' : 'bescherm'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="sub">Je kunt overdag een vuur kiezen om ’s nachts af te schermen.</div>
      )}
    </div>
  );
}

function SchaduwPanel({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const me = view.self;
  const nat = me.natHout;
  const canAim = me.isAlive && (view.game.phase === 'day' || view.game.phase === 'evening');
  const others = view.players.filter((p) => p.isAlive && p.id !== me.id);

  async function aim(targetPlayerId: string) {
    setBusy(targetPlayerId);
    try {
      const r = await api.natHout(code, { targetPlayerId });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'nat hout mislukt');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 10 }}>
      <div className="lab" style={{ margin: '0 0 5px' }}>
        🌫️ Nat hout · één keer per potje
      </div>
      {nat?.spent ? (
        <div className="sub">Je hebt je nat hout dit potje al gespeeld.</div>
      ) : canAim ? (
        <>
          <div className="sub" style={{ marginBottom: 8 }}>
            Mik het op een onschuldige — diens vuur sputtert vannacht en die lijkt de saboteur. Kies
            iemand die een post geclaimd heeft.
          </div>
          <div className="gap8">
            {others.map((p) => {
              const aimed = nat?.pendingTargetId === p.id;
              return (
                <div key={p.id} className="tally">
                  <span>{p.displayName}</span>
                  <button className="vote" disabled={busy !== null} onClick={() => void aim(p.id)}>
                    {busy === p.id ? '…' : aimed ? '✓ gemikt' : 'nat hout'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="sub">Je kunt overdag iemand kiezen om nat hout op te mikken.</div>
      )}
    </div>
  );
}

function SettingsFooter({ view, code }: { view: PlayerGameView; code: string }) {
  // The skip-phase control is a QA/testing knob: host-only AND only in fast
  // clock mode, so a normal realtime family game never surfaces a debug button.
  const showSkip = view.self.isHost && view.game.schedule.clockMode === 'fast';
  return (
    <div className="settings gap8" style={{ marginTop: 20 }}>
      <div className="lab" style={{ margin: 0 }}>
        Spel
      </div>
      <PushBanner code={code} />
      {showSkip && <HostSkip code={code} phase={view.game.phase} />}
      <LeaveGame code={code} />
    </div>
  );
}

function HostSkip({ code, phase }: { code: string; phase: PlayerGameView['game']['phase'] }) {
  const [busy, setBusy] = useState(false);
  async function skip() {
    setBusy(true);
    try {
      await api.adminSkipPhase(code);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'overslaan mislukt');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="btn outline" onClick={() => void skip()} disabled={busy}>
      {busy ? 'bezig…' : `host: ${PHASE_LABEL[phase]} overslaan → volgende fase`}
    </button>
  );
}

function LeaveGame({ code }: { code: string }) {
  const [busy, setBusy] = useState(false);
  async function leave() {
    if (!confirm('Het dorp verlaten? Je kunt niet meer terug in dit potje.')) return;
    setBusy(true);
    try {
      await api.leave(code);
    } catch {
      /* leave anyway */
    } finally {
      navigate({ name: 'splash' });
    }
  }
  return (
    <button className="btn outline danger" onClick={() => void leave()} disabled={busy}>
      dorp verlaten
    </button>
  );
}

function PushBanner({ code }: { code: string }) {
  const [pstate, setPstate] = useState<PushState>(() => getInitialPushState());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pstate === 'granted') void ensureSubscribed(code);
  }, [code, pstate]);
  if (
    pstate === 'unsupported' ||
    pstate === 'no-vapid' ||
    pstate === 'granted' ||
    pstate === 'denied'
  ) {
    return null;
  }
  async function enable() {
    setBusy(true);
    try {
      setPstate(await ensureSubscribed(code));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="push-banner">
      <span>een seintje als de fase wisselt?</span>
      <button onClick={() => void enable()} disabled={busy}>
        {busy ? '…' : 'aanzetten'}
      </button>
    </div>
  );
}

// ---------- Post claim (binary) ----------

function EarnedOrderBanner({ view }: { view: PlayerGameView }) {
  const me = view.self;
  const order = view.claimOrder;
  const total = order.length;
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.displayName ?? '?';
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="lab" style={{ margin: '0 0 5px' }}>
        🏅 Verdiende volgorde
      </div>
      <div className="sub" style={{ marginBottom: 6 }}>
        Minst verdacht kiest eerst. Jij kiest als <strong>{me.claimRank}e</strong> van {total}. Bij
        een vol vuur kun je een lager-geplaatste verdringen.
      </div>
      <ol className="pick-order">
        {order.map((id) => (
          <li key={id} className={id === me.id ? 'me' : ''}>
            {nameOf(id)}
          </li>
        ))}
      </ol>
    </div>
  );
}

function WisseltokenBanner({ view }: { view: PlayerGameView }) {
  const me = view.self;
  const tokens = me.wisseltokens;
  const claimed = me.pendingPost != null;
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="lab" style={{ margin: '0 0 5px' }}>
        🔀 Wisseltoken · {tokens} in bezit
      </div>
      <div className="sub">
        {!claimed ? (
          <>Je eerste claim vannacht is vrij. Daarna ligt hij vast.</>
        ) : tokens > 0 ? (
          <>
            Je claim ligt <strong>vast</strong>. Naar een ander vuur wisselen kost{' '}
            <strong>1 wisseltoken</strong>.
          </>
        ) : (
          <>
            Je claim ligt <strong>vast</strong> en je hebt geen wisseltoken — je blijft vannacht op
            dit vuur.
          </>
        )}
      </div>
    </div>
  );
}

function PostView({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const me = view.self;
  const [busy, setBusy] = useState<string | null>(null);
  // inzet only exists with the inzetSchaal module; default to the middle (2).
  const inzetOn = view.game.modules.inzetSchaal;
  const [inzet, setInzet] = useState(me.pendingPost?.inzet ?? 2);
  // Posts are chosen during the DAY; at dusk (schemer) they lock — you then only
  // see who is standing where. Only 'day' is editable.
  const editable = me.isAlive && view.game.phase === 'day';
  const locked = me.isAlive && view.game.phase === 'evening';
  const shifter = me.team === 'shapeshifters';

  async function claim(fireId: string, action: PostAction) {
    setBusy(`${fireId}:${action}`);
    try {
      const r = await api.claimPost(code, { fireId, action, ...(inzetOn ? { inzet } : {}) });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'claim mislukt');
    } finally {
      setBusy(null);
    }
  }

  if (!me.isAlive) {
    return <DeadNotice />;
  }
  if (!editable && !locked) {
    return (
      <>
        <h2 className="vt">Je post</h2>
        <div className="sub">Je kunt je post claimen zodra de dag begint.</div>
      </>
    );
  }

  return (
    <>
      {/* P1 dedup: the taskbar CTA already says "Kies je post"; this labels the
          section instead of repeating the call to action. */}
      <h2 className="vt">{editable ? 'Jouw post' : 'De posten vannacht'}</h2>
      <div className="sub">
        {editable
          ? 'Claim een vuur voor vannacht. De plekken zijn beperkt.'
          : 'De plekken liggen vast tot de ochtend. Zo staan ze:'}
      </div>

      {editable && view.game.modules.earnedOrder && me.claimRank != null && (
        <EarnedOrderBanner view={view} />
      )}

      {editable && view.game.modules.wisseltoken && <WisseltokenBanner view={view} />}

      {editable && inzetOn && (
        <div className="inzet">
          <span className="lbl">Inzet</span>
          <div className="seg" role="group" aria-label="inzet">
            {[1, 2, 3].map((n) => (
              <button key={n} className={inzet === n ? 'on' : ''} onClick={() => setInzet(n)}>
                {n}
              </button>
            ))}
          </div>
          <span className="lbl">meer inzet = meer effect én meer sporen</span>
        </div>
      )}

      <div className="gap8" style={{ marginTop: 12 }}>
        {view.fires.map((fire) => {
          const selected = me.pendingPost?.fireId === fire.id;
          const myAction = selected ? me.pendingPost?.action : null;
          const gedoofd = fire.state === 'gedoofd';
          const occ = fire.occupantPlayerIds.length;
          const inzetTag = inzetOn && selected ? ` · inzet ${me.pendingPost?.inzet}` : '';
          return (
            <div
              key={fire.id}
              className={`fireopt ${selected ? 'sel' : ''} ${gedoofd ? 'gedoofd' : ''}`}
            >
              <div className="head">
                <div>
                  <div className="nm">
                    {selected ? '✓ ' : ''}
                    {fire.name}
                  </div>
                  <div className="cap">
                    {occ}/{fire.slots} plekken
                    {fire.damage != null && fire.maxDamage != null && fire.damage > 0 && (
                      <>
                        {' '}
                        · {fire.damage}/{fire.maxDamage} schade
                      </>
                    )}
                    {fire.kwetsbaar && <span className="kwetsbaar-tag"> ⚠ kwetsbaar</span>}
                  </div>
                </div>
                <FirePresence fire={fire} players={view.players} selfId={me.id} />
              </div>
              <FireBar fire={fire} />
              {editable &&
                !gedoofd &&
                (shifter ? (
                  <div className="fire-actions">
                    <button
                      className={`feed ${myAction === 'feed' ? 'on' : ''}`}
                      disabled={busy !== null}
                      onClick={() => void claim(fire.id, 'feed')}
                    >
                      🔥 voeden{myAction === 'feed' ? inzetTag : ''}
                    </button>
                    <button
                      className={`sab ${myAction === 'sabotage' ? 'on' : ''}`}
                      disabled={busy !== null}
                      onClick={() => void claim(fire.id, 'sabotage')}
                    >
                      🗡️ saboteren{myAction === 'sabotage' ? inzetTag : ''}
                    </button>
                  </div>
                ) : (
                  <button
                    className={`claim-one ${selected ? 'on' : ''}`}
                    disabled={busy !== null}
                    onClick={() => void claim(fire.id, 'feed')}
                  >
                    {/* P2: neutral label — "voeden" would pre-commit the secret
                        choice; that belongs in the action sheet, not the button. */}
                    {selected ? `✓ jouw post${inzetTag}` : 'hier posten'}
                  </button>
                ))}
              {/* Locked (schemer): no changing — just mark your own post read-only. */}
              {locked && selected && !gedoofd && (
                <div className="claim-one on locked">
                  ✓ jouw post — je {myAction === 'sabotage' ? 'saboteert' : 'voedt'} hier{inzetTag}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="sub" style={{ marginTop: 12 }}>
        Plaatsing is zichtbaar voor iedereen. Wát je daar doet, blijft geheim.
      </div>
    </>
  );
}

// ---------- Vote (evening) ----------

function VoteView({
  view,
  code,
  onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const me = view.self;
  const others = view.players.filter((p) => p.isAlive && p.id !== me.id);
  const maxCount = Math.max(1, ...view.voteTally.map((t) => t.count));
  const countFor = (id: string) => view.voteTally.find((t) => t.targetPlayerId === id)?.count ?? 0;

  async function vote(targetPlayerId: string) {
    if (me.myVoteTargetId === targetPlayerId) return; // already your pick — nothing to change
    setBusy(targetPlayerId);
    try {
      const r = await api.vote(code, { targetPlayerId });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'stem mislukt');
    } finally {
      setBusy(null);
    }
  }

  if (!me.isAlive) return <DeadNotice />;

  if (view.game.phase !== 'evening') {
    return (
      <>
        <h2 className="vt">Stemronde</h2>
        <div className="sub">
          De stemronde is in de <strong>schemer</strong>. Verzamel overdag je verdenkingen — dan
          stem je hier anoniem.
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className="vt">Stemronde</h2>
      <div className="sub">
        {me.hasVotedToday ? (
          <>
            Je stemt op{' '}
            <strong>{others.find((p) => p.id === me.myVoteTargetId)?.displayName ?? '…'}</strong>.
            Je kunt nog wisselen tot de schemer sluit — anoniem, met publieke telling.
          </>
        ) : (
          'Wie sturen we weg? Anoniem, met publieke telling.'
        )}
      </div>
      <div className="sub" style={{ marginTop: 6 }}>
        Wie weg is doet niet meer mee — de rol blijft geheim. Bij gelijkspel valt niemand af.
      </div>

      <div className="gap8" style={{ marginTop: 12 }}>
        {me.isAlive && (
          // Your own row: no vote button (you can't vote for yourself), but you DO
          // see how many votes are stacking up against you.
          <div className="tally self" title="stemmen tegen jou">
            <div className="fillb" style={{ width: `${(countFor(me.id) / maxCount) * 100}%` }} />
            <span className="who">
              <span style={{ color: playerColor(me.id) }}>{me.displayName}</span>
              <span className="jij">jij</span>
            </span>
            <span className="cnt">{countFor(me.id) > 0 ? countFor(me.id) : ''}</span>
          </div>
        )}
        {others.map((p) => {
          const count = countFor(p.id);
          const chosen = me.myVoteTargetId === p.id;
          return (
            <div key={p.id} className={`tally${chosen ? ' chosen' : ''}`}>
              <div className="fillb" style={{ width: `${(count / maxCount) * 100}%` }} />
              <span style={{ color: playerColor(p.id) }}>{p.displayName}</span>
              <span className="cnt">{count > 0 ? count : ''}</span>
              <button
                className={`vote${chosen ? ' chosen' : ''}`}
                disabled={busy !== null || chosen}
                onClick={() => void vote(p.id)}
              >
                {busy === p.id
                  ? '…'
                  : chosen
                    ? '✓ jouw stem'
                    : me.hasVotedToday
                      ? 'wissel'
                      : 'stem'}
              </button>
            </div>
          );
        })}
      </div>

      {me.pendingPost && (
        <div className="sub" style={{ marginTop: 14 }}>
          Je post voor vannacht staat al vast bij{' '}
          <strong>{view.fires.find((f) => f.id === me.pendingPost!.fireId)?.name}</strong>
          {view.game.modules.inzetSchaal ? ` (inzet ${me.pendingPost.inzet})` : ''}.
        </div>
      )}
    </>
  );
}

// ---------- Overleg: all chat in one place, tabstrip (1D) ----------

function OverlegView({
  view,
  code,
  onRefresh,
  typers,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
  typers: Record<string, Record<string, string>>;
}) {
  const [active, setActive] = useState<'plein' | string>('plein');

  // No location chats → Overleg is just the dorpsplein, no tabstrip.
  if (!view.game.modules.locationChats) {
    return (
      <div className="overleg">
        <DorpspleinView view={view} code={code} onRefresh={onRefresh} typers={typers.plein} />
      </div>
    );
  }

  async function goLocation(fireId: string) {
    setActive(fireId);
    // Selecting a location tab moves you there (presence + the enter_at cutoff).
    if (view.self.currentFireId !== fireId) {
      try {
        const r = await api.move(code, { fireId });
        onRefresh(r.view);
      } catch (err) {
        alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'verplaatsen mislukt');
      }
    }
  }

  return (
    <div className="overleg">
      <div className="chat-tabs" role="tablist">
        <button
          className={active === 'plein' ? 'on' : ''}
          onClick={() => setActive('plein')}
          role="tab"
        >
          Dorpsplein
        </button>
        {view.fires.map((f) => (
          <button
            key={f.id}
            className={active === f.id ? 'on' : ''}
            onClick={() => void goLocation(f.id)}
            role="tab"
          >
            {f.name}
          </button>
        ))}
      </div>
      {active === 'plein' ? (
        <DorpspleinView view={view} code={code} onRefresh={onRefresh} typers={typers.plein} />
      ) : (
        <LocationChatView
          view={view}
          code={code}
          fireId={active}
          onRefresh={onRefresh}
          typers={typers[active]}
          embedded
        />
      )}
    </div>
  );
}

// WhatsApp-style time on each bubble (HH:mm) + date dividers between days.
function msgTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}
function dayLabel(iso: string): string {
  const d = new Date(iso);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86_400_000);
  if (diff === 0) return 'Vandaag';
  if (diff === 1) return 'Gisteren';
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
}
function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

// Renders a message stream with WhatsApp-style day dividers between calendar days.
function ChatStream({
  messages,
  players,
  selfId,
}: {
  messages: PlayerGameView['messages'];
  players: PublicPlayer[];
  selfId: string;
}) {
  return (
    <>
      {messages.map((m, i) => {
        const prev = messages[i - 1];
        const showDay = !prev || !sameDay(prev.createdAt, m.createdAt);
        return (
          <Fragment key={m.id}>
            {showDay && (
              <div className="day-sep">
                <span>{dayLabel(m.createdAt)}</span>
              </div>
            )}
            <ChatBubble m={m} players={players} selfId={selfId} />
          </Fragment>
        );
      })}
    </>
  );
}

// A chat bubble carrying the author's team-NEUTRAL identity colour (1D), so the
// same person is easy to follow. The name + a left accent get the colour; the
// bubble stays readable (no loud fill).
function ChatBubble({
  m,
  players,
  selfId,
}: {
  m: PlayerGameView['messages'][number];
  players: PublicPlayer[];
  selfId: string;
}) {
  const mine = m.authorPlayerId === selfId;
  const who = players.find((p) => p.id === m.authorPlayerId)?.displayName ?? '?';
  const col = playerColor(m.authorPlayerId);
  return (
    <div
      className={`bubble ${mine ? 'b-me' : 'b-in'}`}
      style={mine ? undefined : { borderLeftColor: col }}
    >
      {!mine && (
        <div className="who" style={{ color: col }}>
          {who}
        </div>
      )}
      <Mentions body={m.body} players={players} />
      <span className="time">{msgTime(m.createdAt)}</span>
    </div>
  );
}

// "X is aan het typen…" for a channel — everyone currently composing, minus you.
function TypingLine({
  typers,
  selfId,
}: {
  typers: Record<string, string> | undefined;
  selfId: string;
}) {
  const names = Object.entries(typers ?? {})
    .filter(([id]) => id !== selfId)
    .map(([, name]) => name);
  if (names.length === 0) return null;
  const label =
    names.length === 1
      ? `${names[0]} is aan het typen…`
      : names.length === 2
        ? `${names[0]} en ${names[1]} zijn aan het typen…`
        : `${names.length} spelers zijn aan het typen…`;
  return (
    <div className="typing" aria-live="polite">
      <span className="dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label}
    </div>
  );
}

// Throttled "I am composing" ping. Shared by both chat composers. The server
// derives the game + identity from the socket, so only the channel is sent.
function useTypingPing(fireId: string | null) {
  const last = useRef(0);
  return () => {
    const now = Date.now();
    if (now - last.current < 2500) return;
    last.current = now;
    try {
      getSocket().emit('chat:typing', { fireId });
    } catch {
      // no socket / not subscribed — the indicator is best-effort
    }
  };
}

// Test-mode indicator: is this game running bots, and is the Claude brain live?
// Polls the bot-status endpoint; renders nothing for a normal (bot-less) game.
function BotStatus({ code }: { code: string }) {
  const [st, setSt] = useState<BotStatusResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    const tick = () =>
      api
        .botStatus(code)
        .then((s) => {
          if (!cancelled) setSt(s);
        })
        .catch(() => {});
    tick();
    const id = window.setInterval(tick, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [code]);

  if (!st || !st.hasBots) return null;

  let tone: 'green' | 'amber' | 'grey' = 'grey';
  let label = 'Testbots: heuristiek';
  if (st.brain === 'llm') {
    if (st.llmOk === false) {
      tone = 'amber';
      label = 'Claude: terugval op heuristiek';
    } else {
      tone = 'green';
      label = st.calls > 0 ? 'Claude actief' : 'Claude gekoppeld';
    }
  }
  return (
    <div className={`botstat ${tone}`} title="alleen zichtbaar in test-modus met bots">
      <span className="dot" aria-hidden="true" />
      {label}
      {st.brain === 'llm' && st.calls > 0 && (
        <span className="meta">
          {st.calls} calls{st.fallbacks > 0 ? ` · ${st.fallbacks} terugval` : ''}
        </span>
      )}
    </div>
  );
}

// ---------- Dorpsplein ----------

function DorpspleinView({
  view,
  code,
  onRefresh,
  typers,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
  typers?: Record<string, string> | undefined;
}) {
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const me = view.self;
  const ping = useTypingPing(null);

  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [view.messages.length, typers]);

  async function post(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const r = await api.postMessage(code, { body: body.trim() });
      setBody('');
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'bericht mislukt');
    } finally {
      setPosting(false);
    }
  }

  const showTally = view.game.phase === 'evening' && view.voteTally.length > 0;
  const maxCount = Math.max(1, ...view.voteTally.map((t) => t.count));

  return (
    <div className="chatpane">
      <div className="row">
        <h2 className="vt">Dorpsplein</h2>
        <div className="count" style={{ fontSize: 10.5 }}>
          {view.players.filter((p) => p.isAlive).length} in het dorp
        </div>
      </div>

      {showTally && (
        <div className="card" style={{ marginTop: 8 }}>
          <div className="lab" style={{ margin: '0 0 7px' }}>
            Verdenkingen vanavond
          </div>
          <div className="gap8">
            {view.voteTally
              .slice()
              .sort((a, b) => b.count - a.count)
              .map((t) => (
                <div key={t.targetPlayerId} className="tally">
                  <div className="fillb" style={{ width: `${(t.count / maxCount) * 100}%` }} />
                  <span style={{ color: playerColor(t.targetPlayerId) }}>
                    {view.players.find((p) => p.id === t.targetPlayerId)?.displayName}
                  </span>
                  <span className="cnt">{t.count}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="stream" ref={streamRef}>
        {view.messages.length === 0 ? (
          <p className="empty">Nog stil op het plein.</p>
        ) : (
          <ChatStream messages={view.messages} players={view.players} selfId={me.id} />
        )}
      </div>

      <TypingLine typers={typers} selfId={me.id} />

      {me.isAlive && (
        <form className="composer" onSubmit={post}>
          <input
            type="text"
            maxLength={500}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              if (e.target.value.trim()) ping();
            }}
            placeholder="praat mee op het plein…"
          />
          <button
            className="send"
            type="submit"
            disabled={posting || !body.trim()}
            aria-label="verstuur"
          >
            ➤
          </button>
        </form>
      )}
    </div>
  );
}

function Mentions({ body, players }: { body: string; players: PublicPlayer[] }) {
  // Colour each @mention in the mentioned player's identity colour (1D).
  const colByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of players) m.set(p.displayName.toLowerCase(), playerColor(p.id));
    return m;
  }, [players]);
  const parts = body.split(/(@\w+)/g);
  return (
    <>
      {parts.map((part, i) => {
        const col = part.startsWith('@') ? colByName.get(part.slice(1).toLowerCase()) : undefined;
        if (col) {
          return (
            <span key={i} className="ment" style={{ color: col }}>
              {part}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

// ---------- Private location chat (LocationChats module) ----------

function PresenceAvatars({ ids, view }: { ids: string[]; view: PlayerGameView }) {
  if (ids.length === 0) return <span className="empty">leeg</span>;
  return (
    <div className="presence">
      {ids.slice(0, 6).map((id) => (
        <span
          key={id}
          className={`avatar ${id === view.self.id ? 'me' : ''}`}
          style={{ color: playerColor(id), borderColor: playerColor(id) }}
          title={view.players.find((p) => p.id === id)?.displayName ?? ''}
        >
          {initials(view.players.find((p) => p.id === id)?.displayName)}
        </span>
      ))}
    </div>
  );
}

function LocationChatView({
  view,
  code,
  fireId,
  onBack,
  onRefresh,
  typers,
  embedded,
}: {
  view: PlayerGameView;
  code: string;
  fireId: string;
  onBack?: () => void;
  onRefresh: (v: PlayerGameView) => void;
  typers?: Record<string, string> | undefined;
  // Embedded in the Overleg tabstrip → no "‹ kaart" back button (tabs switch).
  embedded?: boolean;
}) {
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const me = view.self;
  const ping = useTypingPing(fireId);
  const fire = view.fires.find((f) => f.id === fireId);
  const present = fire
    ? fire.presentPlayerIds.filter((id) => view.players.find((p) => p.id === id)?.isAlive)
    : [];
  const stillHere = me.currentFireId === fireId;

  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [view.locationMessages.length]);

  async function post(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const r = await api.postMessage(code, { body: body.trim(), fireId });
      setBody('');
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'bericht mislukt');
    } finally {
      setPosting(false);
    }
  }

  async function reenter() {
    try {
      const r = await api.move(code, { fireId });
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'verplaatsen mislukt');
    }
  }

  if (!fire) {
    return (
      <>
        {onBack && !embedded && (
          <button className="back" onClick={onBack}>
            ‹ kaart
          </button>
        )}
        <p className="empty">Dit vuur bestaat niet meer.</p>
      </>
    );
  }

  return (
    <div className="chatpane">
      {onBack && !embedded && (
        <button className="back" onClick={onBack}>
          ‹ kaart
        </button>
      )}
      <div className="row">
        <h2 className="vt">{fire.name}</h2>
        <div className={`st ${fire.state}`} style={{ fontSize: 12 }}>
          <span className={`dot ${fire.state}`} />
          {FIRE_ST_LABEL[fire.state]}
        </div>
      </div>

      <div className="card" style={{ padding: '10px 12px', marginTop: 8 }}>
        <div className="nu-hier">
          <div className="lab" style={{ margin: 0 }}>
            Nu hier
          </div>
          <PresenceAvatars ids={present} view={view} />
        </div>
      </div>

      <div className="enter">je ziet alleen wat ná je aankomst is gezegd</div>

      {stillHere ? (
        <>
          <div className="stream" ref={streamRef}>
            {view.locationMessages.length === 0 ? (
              <p className="empty">Nog niks gefluisterd sinds je aankwam.</p>
            ) : (
              <ChatStream messages={view.locationMessages} players={view.players} selfId={me.id} />
            )}
          </div>
          <TypingLine typers={typers} selfId={me.id} />
          {me.isAlive && (
            <form className="composer" onSubmit={post}>
              <input
                type="text"
                maxLength={500}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  if (e.target.value.trim()) ping();
                }}
                placeholder="fluister hier…"
              />
              <button
                className="send"
                type="submit"
                disabled={posting || !body.trim()}
                aria-label="verstuur"
              >
                ➤
              </button>
            </form>
          )}
        </>
      ) : (
        <div style={{ marginTop: 12 }}>
          <p className="empty">Je staat hier niet meer.</p>
          <button className="btn outline" style={{ marginTop: 10 }} onClick={() => void reenter()}>
            opnieuw naar {fire.name}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Night ----------

function NightView({
  view,
  code,
  onRefresh: _onRefresh,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
}) {
  const me = view.self;
  const myFireId = me.pendingPost?.fireId ?? null;
  return (
    <div className="gm is-night phase-night" style={nightBackdrop}>
      <div className="gm-content">
        <div className="row">
          <h2 className="vt" style={{ color: '#e9ecf1' }}>
            Nacht · dag {view.game.dayNumber}
          </h2>
          <div className="count">op je post</div>
        </div>

        <div style={{ textAlign: 'center', margin: '8px 0 4px' }}>
          <img
            className="moon-art"
            src={NIGHT_MOON_ART}
            alt=""
            aria-hidden="true"
            width={120}
            height={120}
            loading="lazy"
          />
          <div className="sub" style={{ marginTop: 10 }}>
            Lights out. De posten liggen vast tot de ochtend.
          </div>
        </div>

        {me.pendingPost ? (
          <div className="card" style={{ marginTop: 6 }}>
            <div className="lab" style={{ margin: '0 0 4px' }}>
              Jouw post
            </div>
            <div className="sub">
              Je staat bij{' '}
              <strong style={{ color: 'var(--gm-cream)' }}>
                {view.fires.find((f) => f.id === myFireId)?.name}
              </strong>{' '}
              — je {me.pendingPost.action === 'sabotage' ? 'saboteert stiekem' : 'voedt het vuur'}
              {view.game.modules.inzetSchaal ? ` (inzet ${me.pendingPost.inzet})` : ''}. Je kunt
              niet meer wisselen.
            </div>
          </div>
        ) : (
          <p className="empty" style={{ marginTop: 8, textAlign: 'center' }}>
            Je koos geen post. Je slaapt deze nacht door.
          </p>
        )}

        <div className="lab">De posten vannacht</div>
        <div className="gap8">
          {view.fires.map((f) => (
            <div key={f.id} className={`npost ${f.id === myFireId ? 'mine' : ''}`}>
              <div>
                <div className="nm">{f.name}</div>
                <span
                  className={`pill ${f.state}`}
                  style={{ marginTop: 4, display: 'inline-block' }}
                >
                  {FIRE_ST_LABEL[f.state]}
                </span>
                {f.damage != null && f.maxDamage != null && (
                  <Meter damage={f.damage} maxDamage={f.maxDamage} mini />
                )}
              </div>
              <div className="presence">
                {/* 18 — same identity colour as the day list and the chat
                    bubbles; at night "who stands where" is exactly the evidence
                    you'll argue about in the morning. */}
                {f.occupantPlayerIds.slice(0, 5).map((id) => (
                  <span
                    key={id}
                    className={`navatar ${id === me.id ? 'me' : ''}`}
                    style={{ color: playerColor(id), borderColor: playerColor(id) }}
                    title={view.players.find((p) => p.id === id)?.displayName ?? ''}
                  >
                    {initials(view.players.find((p) => p.id === id)?.displayName)}
                  </span>
                ))}
                {f.occupantPlayerIds.length === 0 && <span className="empty">onbewaakt</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="sub" style={{ marginTop: 12 }}>
          Wat er met de vuren gebeurt, zie je in de ochtend.
        </div>

        {me.isHost && view.game.schedule.clockMode === 'fast' && <NightHostSkip code={code} />}
      </div>
    </div>
  );
}

function NightHostSkip({ code }: { code: string }) {
  const [busy, setBusy] = useState(false);
  async function wake() {
    setBusy(true);
    try {
      await api.adminSkipPhase(code);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'skip mislukt');
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      className="btn glow mt-a"
      style={{ marginTop: 16 }}
      onClick={() => void wake()}
      disabled={busy}
    >
      {busy ? 'bezig…' : 'host: de ochtend breekt aan ›'}
    </button>
  );
}

// ---------- Game over ----------

function GameOver({
  view,
  code,
  onRefresh,
  typers,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
  typers?: Record<string, string> | undefined;
}) {
  const winner = view.game.winnerTeam;
  const eerlijken = winner === 'eerlijken';
  const lines = view.events.slice(0, 6);
  return (
    <div className="gm over phase-night">
      <div className="over-wrap">
        <img
          className="end-art"
          src={eerlijken ? END_ART.eerlijken : END_ART.shapeshifters}
          alt={
            eerlijken
              ? 'Willowbrook bij dageraad, alle wachtvuren fel brandend'
              : 'Willowbrook in duisternis, de wachtvuren gedoofd'
          }
          width={420}
          height={525}
        />
        <h1>{eerlijken ? 'De keten hield stand' : 'De duisternis brak door'}</h1>
        <div className={`team ${eerlijken ? 'eerlijken' : 'shapeshifters'}`}>
          {eerlijken ? 'De eerlijken winnen' : 'De shapeshifters winnen'}
        </div>
        <img className="flame-divider" src={DECOR_ART.divider} alt="" loading="lazy" />
        {view.roleReveal && view.roleReveal.length > 0 && (
          <div className="card" style={{ width: '100%', textAlign: 'left' }}>
            <div className="reveal">
              <div className="lab">Wie was wie</div>
              <ul className="rolelist">
                {[...view.roleReveal]
                  // Shapeshifters first — the payoff: who was scheming all along.
                  .sort((a, b) => (a.team === b.team ? 0 : a.team === 'shapeshifters' ? -1 : 1))
                  .map((r) => (
                    <li key={r.playerId} className={r.team === 'shapeshifters' ? 'sh' : 'eer'}>
                      <img
                        className="rr-art"
                        src={ROLE_ART[r.role] ?? ROLE_ART.vuurwachter}
                        alt=""
                        width={30}
                        height={30}
                      />
                      <span className="rr-name" style={{ color: playerColor(r.playerId) }}>
                        {r.displayName}
                        {!r.isAlive && <span className="rr-dead"> · weggestuurd</span>}
                      </span>
                      <span className="rr-role">{ROLE_LABEL[r.role] ?? r.role}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
        <div className="card" style={{ width: '100%', textAlign: 'left' }}>
          <div className="reveal">
            <div className="lab">Het verhaal</div>
            <ul>
              {lines.map((e) => (
                <li key={e.id}>{e.text}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* 2E: keep the dorpsplein open so the group can talk it over. */}
        <PostGameChat view={view} code={code} onRefresh={onRefresh} typers={typers} />

        <button className="btn primary" onClick={() => navigate({ name: 'splash' })}>
          home
        </button>
      </div>
    </div>
  );
}

// Post-game wrap-up chat: the dorpsplein stays open (everyone, alive or not).
function PostGameChat({
  view,
  code,
  onRefresh,
  typers,
}: {
  view: PlayerGameView;
  code: string;
  onRefresh: (v: PlayerGameView) => void;
  typers?: Record<string, string> | undefined;
}) {
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const streamRef = useRef<HTMLDivElement>(null);
  const me = view.self;
  const ping = useTypingPing(null);

  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [view.messages.length, typers]);

  async function post(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      const r = await api.postMessage(code, { body: body.trim() });
      setBody('');
      onRefresh(r.view);
    } catch (err) {
      alert(err instanceof ApiError ? `${err.code}: ${err.message}` : 'bericht mislukt');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="card postgame-chat" style={{ width: '100%', textAlign: 'left' }}>
      <div className="lab" style={{ margin: '0 0 6px' }}>
        Napraten
      </div>
      <div className="stream pg-stream" ref={streamRef}>
        {view.messages.length === 0 ? (
          <p className="empty">Praat na over het potje…</p>
        ) : (
          <ChatStream messages={view.messages} players={view.players} selfId={me.id} />
        )}
      </div>
      <TypingLine typers={typers} selfId={me.id} />
      <form className="composer" onSubmit={post}>
        <input
          type="text"
          maxLength={500}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (e.target.value.trim()) ping();
          }}
          placeholder="praat na op het plein…"
        />
        <button
          className="send"
          type="submit"
          disabled={posting || !body.trim()}
          aria-label="verstuur"
        >
          ➤
        </button>
      </form>
    </div>
  );
}

// ---------- helpers ----------

// The exact damage meter — only rendered when the visibleMeter module hands us
// a number (the firewall sends null otherwise, so this can't leak).
// Remaining fire strength (fuel), not damage: full when brandt, empty at gedoofd.
function fireStrengthPct(damage: number, maxDamage: number): number {
  const health = Math.max(0, maxDamage - damage);
  return Math.max(0, Math.min(100, Math.round((health / maxDamage) * 100)));
}
function fireStrengthLevel(pct: number): 'strong' | 'weak' | 'critical' {
  return pct >= 66 ? 'strong' : pct >= 33 ? 'weak' : 'critical';
}

function Meter({ damage, maxDamage, mini }: { damage: number; maxDamage: number; mini?: boolean }) {
  const pct = fireStrengthPct(damage, maxDamage);
  const level = fireStrengthLevel(pct);
  return (
    <div
      className={`meter ${mini ? 'mini' : ''}`}
      role="meter"
      aria-valuenow={maxDamage - damage}
      aria-valuemin={0}
      aria-valuemax={maxDamage}
      aria-label="vuurkracht"
    >
      <i className={level} style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * Fire intensity bar. Granularity follows the DATA the firewall hands us, not the
 * preset name: with the visibleMeter module on we get an exact `damage/maxDamage`
 * (Uitdaging only) and draw a continuous bar; otherwise `damage` is null by design
 * and we fall back to three fixed stands from `state`.
 */
export function fireBarPct(fire: Pick<PublicFire, 'state' | 'damage' | 'maxDamage'>): number {
  if (fire.damage != null && fire.maxDamage != null) {
    return fireStrengthPct(fire.damage, fire.maxDamage);
  }
  // Introductie/Familie/Klassiek: three fixed stands. Gedoofd draws a full but
  // cold, striped bar — "out", not "empty/unknown".
  return fire.state === 'brandt' ? 92 : fire.state === 'flikkert' ? 42 : 100;
}

// Colour + width + the WORD together, so the state survives without colour
// (colour-blind / older eyes). The list carries no fire icon, so this reinforces
// the word rather than adding a fourth encoding.
function FireBar({ fire }: { fire: PublicFire }) {
  const pct = fireBarPct(fire);
  return (
    <div className="meter-row">
      <div
        className={`meter fs-${fire.state}`}
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`vuurkracht: ${FIRE_ST_LABEL[fire.state]}`}
      >
        <i style={{ width: `${pct}%` }} />
      </div>
      <span className={`state fs-${fire.state}`}>{FIRE_ST_LABEL[fire.state]}</span>
    </div>
  );
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  // Strip emoji/symbols first — bot names carry a 🤖 prefix, which otherwise
  // became the "initial" and rendered as a tofu box next to the real letter.
  const clean = name.replace(/[^\p{L}\p{N}\s]/gu, '').trim();
  if (!clean) return '?';
  if (clean.length <= 2) return clean.toUpperCase();
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}

function useCountdown(iso: string): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'zo…';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}u ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function DeadNotice() {
  return (
    <>
      <h2 className="vt">Je bent weggestuurd</h2>
      <div className="sub">
        Je kijkt nog mee, maar kunt niet meer posten, praten of stemmen. Kijk hoe het afloopt.
      </div>
    </>
  );
}
